import { joinUrl, type ProbeFetch, type ProbeFetchResult } from '@visious/core';

/**
 * §3's probe fetcher — the platform half of `instance/probe.ts`, and the only place in this app that
 * calls `fetch` outside the transport.
 *
 * That exception is deliberate and it is three separate reasons, none of which the transport can serve:
 *
 * 1. **It targets an instance the app is not configured for.** `ApiTransport` is built per
 *    `InstanceConfig`; probing a candidate through the *configured* instance's transport would mean
 *    either building a second platform (the thing `getPlatform()` exists to prevent) or
 *    reconfiguring the live one mid-session.
 * 2. **It has to tell CORS from downtime**, which §3 asks for explicitly: *"A CORS-blocked `fetch` is
 *    indistinguishable from a network error in JS. When step 1 fails, retry as `mode: 'no-cors'` — if
 *    *that* resolves, it's CORS, not downtime."* The transport maps both to `network`, correctly for
 *    its purpose and uselessly for this one. And it is the single most likely failure for a
 *    third-party instance: §3 says web support requires `ACAO: *` or an explicit allowance, so "your
 *    instance is up and will not talk to this origin" is the sentence a reader most needs.
 * 3. **It must not retry.** §3's backoff is right for a page that needs data; a probe that silently
 *    made four attempts per step would take half a minute to report an instance that is simply down,
 *    and would hide the difference between flaky and dead.
 *
 * **No credential, ever.** `credentials: 'omit'` and no `Authorization` header: R9 keys the token to
 * one instance origin, and a probe is by definition pointed somewhere else. The token store is not
 * even imported here.
 */

/** Requests are made with this ceiling so a black-holed instance cannot hang the page. */
export const PROBE_TIMEOUT_MS = 12_000;

function resultOf(response: Response, body: string): ProbeFetchResult {
  return {
    ok: true,
    status: response.status,
    body,
    contentType: response.headers.get('content-type'),
  };
}

/**
 * Was this a CORS refusal rather than an unreachable host? Asked only after a failure, and answered
 * the one way a browser allows: repeat the request with `mode: 'no-cors'`, which skips the CORS check
 * and resolves with an opaque response if *anything* answered.
 *
 * An opaque response carries no status and no body — which is exactly why it cannot be the probe's
 * normal path, and exactly enough to answer this question.
 */
async function looksLikeCors(url: string, signal: AbortSignal): Promise<boolean> {
  try {
    await fetch(url, { mode: 'no-cors', credentials: 'omit', cache: 'no-store', signal });
    return true;
  } catch {
    return false;
  }
}

/**
 * A {@link ProbeFetch} bound to one base URL. Paths in, canned results out — core does the deciding.
 *
 * `signal` is the caller's (the settings page cancels a probe when the reader edits the URL again), and
 * it is combined with a per-request timeout so one dead step cannot hold the whole probe open.
 */
export function createProbeFetch(baseUrl: string, signal?: AbortSignal): ProbeFetch {
  return async (path: string): Promise<ProbeFetchResult> => {
    const url = joinUrl(baseUrl, path);
    if (url === null) return { ok: false, kind: 'network' };

    const timeout = AbortSignal.timeout(PROBE_TIMEOUT_MS);
    const combined = signal === undefined ? timeout : AbortSignal.any([signal, timeout]);

    try {
      const response = await fetch(url, {
        // Explicit rather than default: this request is cross-origin by design and the failure mode
        // being measured is the CORS one.
        mode: 'cors',
        credentials: 'omit',
        cache: 'no-store',
        signal: combined,
      });
      return resultOf(response, await response.text());
    } catch {
      // The caller cancelling and the instance being slow are different answers, and only the
      // caller's own signal distinguishes them — `AbortSignal.timeout` looks identical here.
      if (signal?.aborted === true) return { ok: false, kind: 'aborted' };
      return { ok: false, kind: (await looksLikeCors(url, combined)) ? 'cors' : 'network' };
    }
  };
}
